import React from 'react';

import type { HeadProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import checkInGreenIcon from 'stile-shared/assets/images/inside-stiles-lessons/lesson-type-icons/check-in-green.svg';
import testGreenIcon from 'stile-shared/assets/images/inside-stiles-lessons/lesson-type-icons/test-green.svg';
import prepareTeachAnalyse from 'stile-shared/assets/videos/prepare-teach-analyse.mp4';

import { LessonType } from 'stile-shared/src/components/2024/common/ExpandableLessonList';
import LessonItem from 'stile-shared/src/components/2024/common/ExpandableLessonList/LessonItem';
import { ExploreCurriculum } from 'stile-shared/src/components/2024/common/ExploreCurriculum';
import { VideoPlayer } from 'stile-shared/src/components/2024/common/VideoPlayer';
import { SideBySideSection } from 'stile-shared/src/components/2024/layout/SideBySideSection';

import { PageLayout } from 'templates/2024/PageLayout';
import { SEO } from 'templates/2024/SEO';

export function Head(props: HeadProps) {
  return (
    <>
      <SEO slug={props.location.pathname} />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        href="https://fonts.googleapis.com/css2?family=Shantell+Sans:wght@700&display=swap"
        rel="stylesheet"
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const AssessmentsPageLayout = styled(PageLayout)`
  .header-wrapper {
    background-color: var(--white) !important;
    box-shadow: 0px 78px 22px 0px rgba(0, 0, 0, 0), 0px 50px 20px 0px rgba(0, 0, 0, 0),
      0px 28px 17px 0px rgba(0, 0, 0, 0.02), 0px 12px 12px 0px rgba(0, 0, 0, 0.03),
      0px 3px 7px 0px rgba(0, 0, 0, 0.03);
    @media (max-width: 768px) {
      background-color: var(--stile-green-2) !important;
      box-shadow: unset !important;
    }
  }
`;

/** Section 1 – Green hero banner */
const HeaderSection = styled.div`
  background-color: var(--stile-green-2);
  padding: 100px 0;

  @media (max-width: 768px) {
    padding: 17px 0 60px 0;
  }

  .header-content {
    max-width: 1160px;
    margin: 0 auto;
    padding: 0 25px;
    text-align: center;
  }

  h1 {
    color: var(--stile-grey-8);
    margin: auto;
    max-width: 700px;
  }

  h4 {
    color: var(--white);
    margin: 32px auto 0 auto;
    max-width: 562px;

    @media (max-width: 768px) {
      margin: 25px auto 0 auto;
    }
  }
`;

/** Section 2 – Centred intro copy */
const IntroSection = styled.div`
  background-color: var(--white);
  padding: 150px 0 80px;

  @media (max-width: 768px) {
    padding: 60px 0 40px;
  }

  .content {
    max-width: 761px;
    margin: 0 auto;
    padding: 0 25px;
    text-align: center;
  }

  h2 {
    margin: 0 auto 32px;
  }

  p {
    max-width: 563px;
    margin: 0 auto;
    font-size: 16px;
    line-height: 24px;
    color: var(--stile-grey-8);
  }
`;

/** Section 3 – Assessment system diagram */
const DiagramSection = styled.div`
  background-color: var(--white);
  padding: 0 0 100px;

  @media (max-width: 768px) {
    padding: 0 0 40px;
  }

  .content {
    max-width: 1160px;
    margin: 0 auto;
    padding: 0 25px;
    display: flex;
    justify-content: center;
  }
`;

const DiagramContainer = styled.div`
  width: 100%;
  background: #fff9eb;
  border: 2px solid var(--stile-green-2);
  border-radius: 32px;
  overflow: hidden;

  @media (max-width: 768px) {
    border-radius: 20px;
  }
`;

const DiagramTimeline = styled.div`
  background: linear-gradient(to right, #015221, #00e65a);
  display: flex;
  align-items: center;
  height: 36px;

  .timeline-track {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0 25px;
    position: relative;
  }

  .tl-dot {
    width: 10px;
    height: 10px;
    background: var(--white);
    border-radius: 50%;
    flex-shrink: 0;
  }

  .tl-line-fixed-left {
    width: 60px;
    height: 2px;
    background: var(--white);
    flex-shrink: 0;
    margin-left: -5px;
  }

  .tl-line-fixed-right {
    width: 60px;
    height: 2px;
    background: var(--white);
    flex-shrink: 0;
    margin-right: -5px;
  }

  .tl-line-flex {
    flex: 1;
    height: 2px;
    background: var(--white);
  }

  .tl-gap {
    width: 12px;
    flex-shrink: 0;
  }

  .tl-label {
    font-family: 'Graphik', sans-serif;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    color: var(--white);
    white-space: nowrap;
    flex-shrink: 0;
  }

  .tl-label-middle {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    font-family: 'Graphik', sans-serif;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    color: var(--white);
    white-space: nowrap;
    padding: 0 12px;
    z-index: 1;
    background: linear-gradient(to right, #015221, #00e65a);
    background-size: 500% 100%;
    background-position: 50% 0;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const DiagramContent = styled.div`
  padding: 28px 40px 40px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    padding: 24px 20px 32px;
  }
`;

const DiagramTitle = styled.h3`
  text-align: center;
  font-family: 'Boing', sans-serif;
  font-weight: 500;
  font-size: 32px;
  line-height: 1.25;
  letter-spacing: -0.352px;
  color: var(--stile-grey-8);
  margin: 0 0 36px;

  @media (max-width: 768px) {
    font-size: 32px;
    margin: 0 0 24px;
  }
`;

const DiagramColumns = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;
  min-width: 800px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 32px;
    min-width: 0;
  }
`;

const DiagramColumn = styled.div<{ $hasBorders?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;

  ${({ $hasBorders }) =>
    $hasBorders &&
    `
    border-left: 1px dashed var(--stile-green-2);
    border-right: 1px dashed var(--stile-green-2);
    padding: 0 24px;

    @media (max-width: 768px) {
      border-left: none;
      border-right: none;
      border-top: 1px dashed var(--stile-green-2);
      border-bottom: 1px dashed var(--stile-green-2);
      padding: 32px 0;
    }
  `}
`;

const DiagramPill = styled.div`
  background: var(--stile-grey-8);
  border-radius: 25px;
  padding: 8px 40px;
  width: 234px;
  text-align: center;

  span {
    font-family: 'Shantell Sans', cursive;
    font-weight: 700;
    font-size: 24px;
    line-height: 26px;
    color: var(--white);
  }

  @media (max-width: 768px) {
    width: auto;
    min-width: 200px;
  }
`;

const DiagramItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  width: 100%;
`;

const DiagramItem = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;

  .diagram-item-image {
    flex-shrink: 0;
    width: 104px;
    height: 104px;

    .gatsby-image-wrapper,
    img {
      width: 104px;
      height: 104px;
      object-fit: cover;
    }

    @media (max-width: 768px) {
      width: 104px;
      height: 104px;

      .gatsby-image-wrapper,
      img {
        width: 104px;
        height: 104px;
      }
    }
  }

  .diagram-item-copy {
    h6 {
      font-family: 'Graphik', sans-serif;
      font-weight: 600;
      font-size: 16px;
      line-height: 24px;
      color: var(--stile-grey-8);
      margin: 0 0 8px;
    }

    p {
      font-family: 'Graphik', sans-serif;
      font-weight: 400;
      font-size: 14px;
      line-height: 21px;
      color: var(--stile-grey-8);
      margin: 0;
    }
  }
`;

/** Section 4 – Lesson examples list */
const LessonExamplesSection = styled.div`
  background-color: var(--white);
  padding: 0 0 80px;

  @media (max-width: 768px) {
    padding: 0 0 40px;
  }

  .content {
    max-width: 1160px;
    margin: 0 auto;
    padding: 0 25px;
  }

  .section-label {
    color: var(--stile-green-3);
    font-family: 'Graphik', sans-serif;
    font-weight: 600;
    font-size: 21px;
    line-height: 26px;
    margin: 0 0 32px;

    @media (max-width: 768px) {
      font-size: 18px;
      margin: 0 0 24px;
    }
  }

  .lessons-list {
    background-color: var(--white);

    > *:last-child {
      border-bottom: 1px solid var(--stile-grey-2);
    }
  }
`;

/** Section 5 – "Harness assessments" centred text + image */
const HarnessSection = styled.div`
  background-color: var(--white);
  padding: 80px 0;

  @media (max-width: 768px) {
    padding: 60px 0 20px;
  }

  .content {
    max-width: 761px;
    margin: 0 auto;
    padding: 0 25px;
    text-align: center;
  }

  h2 {
    margin: 0 auto 32px;
  }

  p {
    max-width: 563px;
    margin: 0 auto;
    font-size: 16px;
    line-height: 24px;
    color: var(--stile-grey-8);
  }
`;

const HarnessImageSection = styled.div`
  background-color: var(--white);

  .content {
    max-width: 761px;
    margin: 0 auto;
    padding: 0 25px;
  }
`;

/**
 * Section 6 – side-by-side feature rows on the shared SideBySideSection.
 * Custom vs the base: the large left-aligned 55px h2, tighter body copy, and
 * the image radius + shadow (the base component ships no media elevation).
 * Vertical padding is asymmetric between the two rows (150/75 then 75/150) so
 * they interlock into a consistent 150px rhythm, and both stack text-first on
 * mobile via the shared `text-first-mobile` class.
 */
const AssessmentsRow = styled(SideBySideSection)`
  padding: 150px 0 75px;
  @media (max-width: 768px) {
    padding: 60px 0 40px;
  }

  h2 {
    text-align: left;
    margin-bottom: 32px;
    font-size: 55px;
    line-height: 60px;
    @media (max-width: 768px) {
      font-size: 32px;
      line-height: 38px;
      margin-bottom: 20px;
    }
  }
  p {
    font-size: 16px;
    line-height: 24px;
    color: var(--stile-grey-8);
    margin: 0;
  }
  .left-img {
    margin-top: 0;
  }
  .left-img .gatsby-image-wrapper,
  .right .gatsby-image-wrapper {
    border-radius: 32px;
    box-shadow: 0px 43px 26px 0px rgba(0, 0, 0, 0.04), 0px 19px 19px 0px rgba(0, 0, 0, 0.06),
      0px 5px 11px 0px rgba(0, 0, 0, 0.07);
    @media (max-width: 768px) {
      border-radius: 20px;
    }
  }
`;

const AssessmentsRowLast = styled(AssessmentsRow)`
  padding: 75px 0 150px;
  @media (max-width: 768px) {
    padding: 40px 0 60px;
  }
`;

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

function AssessmentsPage() {
  const exploreCurriculumImage = (
    <StaticImage
      src="../../components/our-principles/images/teachersaying_quality_4.png"
      alt="Stacked documents with celestial diagram showing sun and orbiting planets"
      placeholder="blurred"
      layout="constrained"
      width={124}
      height={124}
    />
  );

  return (
    <AssessmentsPageLayout>
      {/* ---------------------------------------------------------------- */}
      {/* Section 1 – Hero */}
      {/* ---------------------------------------------------------------- */}
      <HeaderSection>
        <div className="header-content">
          <h1 className="--jumbo">A complete system of assessment</h1>
          <h4>Teach with a real-time pulse on student understanding</h4>
        </div>
      </HeaderSection>

      {/* ---------------------------------------------------------------- */}
      {/* Section 2 – Centred intro */}
      {/* ---------------------------------------------------------------- */}
      <IntroSection>
        <div className="content">
          <h2>Assessment to inform instruction</h2>
          <p>
            Assessment is carefully woven all the way through Stile&apos;s units. Teachers then use
            student data to shape their lessons — where to slow down and fill knowledge gaps, where
            to pick up the pace, and where to extend students from their current point of
            understanding.
          </p>
          <br />
          <p>
            Assessment data is neatly stored and summarised in Stile&apos;s Markbook, which
            integrates with School Information Systems.
          </p>
        </div>
      </IntroSection>

      {/* ---------------------------------------------------------------- */}
      {/* Section 3 – Assessment system diagram */}
      {/* ---------------------------------------------------------------- */}
      <DiagramSection>
        <div className="content">
          <DiagramContainer>
            <DiagramTimeline>
              <div className="timeline-track">
                <div className="tl-dot" />
                <div className="tl-line-fixed-left" />
                <div className="tl-gap" />
                <span className="tl-label">Beginning</span>
                <div className="tl-gap" />
                <div className="tl-line-flex" />
                <div className="tl-gap" />
                <span className="tl-label">End</span>
                <div className="tl-gap" />
                <div className="tl-line-fixed-right" />
                <div className="tl-dot" />
                <span className="tl-label-middle">Middle of the unit</span>
              </div>
            </DiagramTimeline>

            <DiagramContent>
              <DiagramTitle>Stile&apos;s system of assessment</DiagramTitle>

              <DiagramColumns>
                {/* Diagnostic column */}
                <DiagramColumn>
                  <DiagramPill>
                    <span>Diagnostic</span>
                  </DiagramPill>
                  <DiagramItemList>
                    <DiagramItem>
                      <div className="diagram-item-image">
                        <StaticImage
                          src="../../../../stile-shared/assets/images/assessments/pretest.png"
                          alt="Pre-test illustration"
                          width={104}
                          height={104}
                          placeholder="blurred"
                        />
                      </div>
                      <div className="diagram-item-copy">
                        <h6>Pre-test</h6>
                        <p>Identify gaps in prior knowledge before embarking on a new unit.</p>
                      </div>
                    </DiagramItem>
                    <DiagramItem>
                      <div className="diagram-item-image">
                        <StaticImage
                          src="../../../../stile-shared/assets/images/assessments/questions-board.png"
                          alt="Questions board illustration"
                          width={104}
                          height={104}
                          placeholder="blurred"
                        />
                      </div>
                      <div className="diagram-item-copy">
                        <h6>Questions board</h6>
                        <p>Turn areas of student curiosity into drivers of learning.</p>
                      </div>
                    </DiagramItem>
                  </DiagramItemList>
                </DiagramColumn>

                {/* Formative column */}
                <DiagramColumn $hasBorders>
                  <DiagramPill>
                    <span>Formative</span>
                  </DiagramPill>
                  <DiagramItemList>
                    <DiagramItem>
                      <div className="diagram-item-image">
                        <StaticImage
                          src="../../../../stile-shared/assets/images/assessments/key-questions.png"
                          alt="Key questions illustration"
                          width={104}
                          height={104}
                          placeholder="blurred"
                        />
                      </div>
                      <div className="diagram-item-copy">
                        <h6>Key questions</h6>
                        <p>
                          Every lesson contains Key Questions that require students to demonstrate
                          understanding of the learning goals.
                        </p>
                      </div>
                    </DiagramItem>
                    <DiagramItem>
                      <div className="diagram-item-image">
                        <StaticImage
                          src="../../../../stile-shared/assets/images/assessments/check-ins.png"
                          alt="Check-ins illustration"
                          width={104}
                          height={104}
                          placeholder="blurred"
                        />
                      </div>
                      <div className="diagram-item-copy">
                        <h6>Check-ins</h6>
                        <p>
                          Auto-marked check-ins, often used as exit tickets, provide teachers with a
                          class-wide snapshot of student retention.
                        </p>
                      </div>
                    </DiagramItem>
                    <DiagramItem>
                      <div className="diagram-item-image">
                        <StaticImage
                          src="../../../../stile-shared/assets/images/assessments/feedback-review.png"
                          alt="Flexibility to review student work illustration"
                          width={104}
                          height={104}
                          placeholder="blurred"
                        />
                      </div>
                      <div className="diagram-item-copy">
                        <h6>Flexibility to review any piece of student work</h6>
                        <p>
                          Dive in on any student response to any question to help students when
                          they&apos;re stuck.
                        </p>
                      </div>
                    </DiagramItem>
                  </DiagramItemList>
                </DiagramColumn>

                {/* Summative column */}
                <DiagramColumn>
                  <DiagramPill>
                    <span>Summative</span>
                  </DiagramPill>
                  <DiagramItemList>
                    <DiagramItem>
                      <div className="diagram-item-image">
                        <StaticImage
                          src="../../../../stile-shared/assets/images/assessments/culminating-activity.png"
                          alt="Culminating activity illustration"
                          width={104}
                          height={104}
                          placeholder="blurred"
                        />
                      </div>
                      <div className="diagram-item-copy">
                        <h6>Culminating activity</h6>
                        <p>
                          Students demonstrate performance expectations, usually entailing extended
                          abstract thinking, creativity and communication.
                        </p>
                      </div>
                    </DiagramItem>
                    <DiagramItem>
                      <div className="diagram-item-image">
                        <StaticImage
                          src="../../../../stile-shared/assets/images/assessments/end-of-unit-test.png"
                          alt="End-of-unit test illustration"
                          width={104}
                          height={104}
                          placeholder="blurred"
                        />
                      </div>
                      <div className="diagram-item-copy">
                        <h6>End-of-unit test</h6>
                        <p>
                          Students demonstrate three-dimensional proficiency across all standards
                          covered in the unit.
                        </p>
                      </div>
                    </DiagramItem>
                  </DiagramItemList>
                </DiagramColumn>
              </DiagramColumns>
            </DiagramContent>
          </DiagramContainer>
        </div>
      </DiagramSection>

      {/* ---------------------------------------------------------------- */}
      {/* Section 4 – Lesson examples */}
      {/* ---------------------------------------------------------------- */}
      <LessonExamplesSection>
        <div className="content">
          <div className="section-label">Examples within Stile</div>
          <div className="lessons-list">
            <LessonItem
              title="Pre-test: Our place in space"
              lessonType={LessonType.PRE_TEST}
              caption="Learn what students know about the topic before you start."
              previewLink="https://stileapp.com/go/pretest_example_1"
              image={
                <StaticImage
                  src="../../../../stile-shared/assets/images/assessments/lesson-pretest.png"
                  alt="Pre-test: Our place in space"
                  width={80}
                  height={56}
                />
              }
            />
            <LessonItem
              title="Questions board: Classification and biodiversity"
              lessonType={LessonType.QUESTION_BOARD}
              caption="Students reflect on the unit's Guiding Question and set up a Questions Board."
              previewLink="https://stileapp.com/go/queboard_example_1"
              image={
                <StaticImage
                  src="../../../../stile-shared/assets/images/assessments/lesson-questions-board.png"
                  alt="Questions board: Classification and biodiversity"
                  width={80}
                  height={56}
                />
              }
            />
            <LessonItem
              title="3.3 Digital signals"
              lessonType={LessonType.KEY_QUESTION}
              caption="Assess if students demonstrate understanding of digital signals."
              previewLink="https://stileapp.com/go/keyq_example_1"
              image={
                <StaticImage
                  src="../../../../stile-shared/assets/images/assessments/lesson-digital-signals.png"
                  alt="3.3 Digital signals"
                  width={80}
                  height={56}
                />
              }
            />
            <LessonItem
              title="1.2 Check-in: Forms of energy"
              lessonType={LessonType.CHECK_IN}
              iconSrc={checkInGreenIcon}
              caption='Formative assessment for the "Forms of energy" lesson.'
              previewLink="https://stileapp.com/go/checkin_example_1"
              image={
                <StaticImage
                  src="../../../../stile-shared/assets/images/assessments/lesson-check-in.png"
                  alt="1.2 Check-in: Forms of energy"
                  width={80}
                  height={56}
                />
              }
            />
            <LessonItem
              title="3.3 Socratic seminar: The Congo dam project"
              lessonType={LessonType.CULMINATING_ACTIVITY}
              caption="Summative task to recommend actions for the proposed Congo dam."
              previewLink="https://stileapp.com/go/culact_example_1"
              image={
                <StaticImage
                  src="../../../../stile-shared/assets/images/assessments/lesson-socratic-seminar.png"
                  alt="3.3 Socratic seminar: The Congo dam project"
                  width={80}
                  height={56}
                />
              }
            />
            <LessonItem
              title="Test: The survival of species"
              lessonType={LessonType.TEST}
              iconSrc={testGreenIcon}
              caption="Summative assessment for the core content of the unit."
              previewLink="https://stileapp.com/go/test_example_1"
              image={
                <StaticImage
                  src="../../../../stile-shared/assets/images/assessments/lesson-test.png"
                  alt="Test: The survival of species"
                  width={80}
                  height={56}
                />
              }
            />
          </div>
        </div>
      </LessonExamplesSection>

      {/* ---------------------------------------------------------------- */}
      {/* Section 5 – Harness assessments */}
      {/* ---------------------------------------------------------------- */}
      <HarnessSection>
        <div className="content">
          <h2>
            Harness assessments
            <br />
            at every stage
          </h2>
          <p>
            Stile&apos;s <strong>Prepare, Teach, Analyse</strong> workflow is the daily rhythm of
            teaching. Use <strong>Prepare</strong> mode to modify lessons informed by assessment
            information from the previous lesson. Use <strong>Teach</strong> mode to catch and
            address misconceptions with your students in front of you. Use <strong>Analyse</strong>{' '}
            mode to identify what you need to consolidate in tomorrow&apos;s lesson.
          </p>
        </div>
      </HarnessSection>

      <HarnessImageSection>
        <div className="content">
          <VideoPlayer url={prepareTeachAnalyse} borderRadius="0px" />
        </div>
      </HarnessImageSection>

      {/* ---------------------------------------------------------------- */}
      {/* Section 6 – Side-by-side feature rows */}
      {/* ---------------------------------------------------------------- */}
      <AssessmentsRow>
        <div className="image-right text-first-mobile content">
          <div className="left-text">
            <h2>Track progress in the Markbook</h2>
            <p>
              All student work for every lesson is neatly stored in Stile&apos;s Markbook. Track
              student grades over time, see who has submitted lessons for review and who
              hasn&apos;t, and dive into student responses to any question in any lesson. All data
              is securely backed up and can be sent to your Student Information System.
            </p>
          </div>
          <div className="right">
            <StaticImage
              src="../../../../stile-shared/assets/images/assessments/markbook.jpg"
              alt="Stile's Markbook showing student grades and submission status"
              placeholder="blurred"
              layout="constrained"
              width={566}
            />
          </div>
        </div>
      </AssessmentsRow>

      <AssessmentsRowLast>
        <div className="image-left text-first-mobile content">
          <div className="left-img">
            <StaticImage
              src="../../../../stile-shared/assets/images/assessments/analytics.jpg"
              alt="Standards-based analytics showing student mastery across standards"
              placeholder="blurred"
              layout="constrained"
              width={566}
            />
          </div>
          <div className="right-text">
            <h2>Learn from standards-based analytics</h2>
            <p>
              Every question in Stile is tagged as &ldquo;developing&rdquo; or &ldquo;showing
              understanding&rdquo; for your State&apos;s standards. As students respond to
              questions, Stile builds a map of their mastery for every standard. Areas of strength
              can be celebrated, and areas of weakness can be targeted for improvement with
              Stile&apos;s supplemental library of &ldquo;power up&rdquo; lessons.
            </p>
          </div>
        </div>
      </AssessmentsRowLast>

      {/* ---------------------------------------------------------------- */}
      {/* Section 7 – Explore curriculum footer */}
      {/* ---------------------------------------------------------------- */}
      <ExploreCurriculum
        title="Explore our core science curriculum"
        image={exploreCurriculumImage}
        exploreCardsData={[
          {
            title: 'Lessons',
            description:
              'Our customisable and interactive lessons are the perfect mix of print, hands-on and digital.',
            url: '/what-is-stile/inside-stiles-lessons/',
          },
          {
            title: 'Stile X Workbooks',
            description:
              'Designed for hands-on learning and perfectly matched to the digital curriculum.',
            url: '/what-is-stile/stile-x/',
          },
          {
            title: 'Pedagogies',
            description: 'Weaving evidence-based strategies into every lesson.',
            url: '/pedagogies/5e-instructional-model/',
          },
        ]}
      />
    </AssessmentsPageLayout>
  );
}

export default AssessmentsPage;
